import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Utility functions for consistent date formatting across the app
export function formatDate(date: string | Date): string {
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return format(dateObj, "dd-MMM-yyyy");
  } catch {
    return typeof date === "string" ? date : "Invalid Date";
  }
}

/**
 * Formats a given date input into a readable 'dd-MMM-yyyy HH:mm:ss' string.
 * @example
 * formatDateTime('2023-10-05T14:48:00')
 * '05-Oct-2023 14:48:00'
 * @param {string | Date} date - The date input, either as a string or Date object.
 * @returns {string} Formatted date-time string or 'Invalid Date' if the input is invalid.
 * @description
 *   - Handles both string and Date object inputs by determining their type.
 *   - Warns with a console message if the input date is invalid.
 *   - Returns the original string or 'Invalid Date' in case of formatting errors.
 */
export function formatDateTime(date: string | Date): string {
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;

    // Check if the date is valid
    if (Number.isNaN(dateObj.getTime())) {
      console.warn("Invalid date provided to formatDateTime:", date);
      return "Invalid Date";
    }

    const dateFormatted = format(dateObj, "dd-MMM-yyyy");
    const timeFormatted = format(dateObj, "HH:mm:ss");
    return `${dateFormatted} ${timeFormatted}`;
  } catch (error) {
    console.error("Error formatting date:", date, error);
    return typeof date === "string" ? date : "Invalid Date";
  }
}

export function formatTime(date: string | Date): string {
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return format(dateObj, "HH:mm:ss");
  } catch {
    return typeof date === "string" ? date : "Invalid Time";
  }
}

function toValidDate(date: string | Date): Date | null {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  if (!isValid(dateObj)) {
    const fallback = typeof date === "string" ? new Date(date) : dateObj;
    return isValid(fallback) ? fallback : null;
  }
  return dateObj;
}

/** Admin/system tables: 10 Jun 2026, 18:04 */
export function formatAdminDate(date: string | Date): string {
  const dateObj = toValidDate(date);
  if (!dateObj) {
    return typeof date === "string" ? date : "Invalid Date";
  }
  return format(dateObj, "dd MMM yyyy, HH:mm");
}

/** Modal/detail views: 10 Jun 2026 at 18:04:03 */
export function formatAdminDateLong(date: string | Date): string {
  const dateObj = toValidDate(date);
  if (!dateObj) {
    return typeof date === "string" ? date : "Invalid Date";
  }
  return `${format(dateObj, "dd MMM yyyy")} at ${format(dateObj, "HH:mm:ss")}`;
}

/** Recent activity labels: 2 minutes ago */
export function formatAdminDateRelative(date: string | Date): string {
  const dateObj = toValidDate(date);
  if (!dateObj) {
    return typeof date === "string" ? date : "Invalid Date";
  }

  const hoursSince = (Date.now() - dateObj.getTime()) / (1000 * 60 * 60);
  if (hoursSince < 24) {
    return formatDistanceToNow(dateObj, { addSuffix: true });
  }

  return formatAdminDate(dateObj);
}
